import React, { useEffect, useRef, useState } from "react";
import "../../../styles/utils/loading/QXQuizLoading.scss";
import ProgressBar from "../ProgressBar";
import OngoingModal from "../modal/OngoingModal";
import oxQuizQuestion from "../../../mockData/oxQuizQuestion.json";
import useAppTranslation from "../../../hooks/useAppTranslation";
import { useRive } from "@rive-app/react-canvas";
import { getLocalizedAssetPath } from "../../../utils/getLocalizedLogo";

const OX_QUIZ_RIVE_PATHS = {
  default: "/riv/oxQuiz/ox-quiz.riv",
  correct: "/riv/oxQuiz/ox-correct.riv",
  wrong: "/riv/oxQuiz/ox-wrong.riv",
};

const OxQuizRive = ({ status, language }) => {
  const src = OX_QUIZ_RIVE_PATHS[status] || OX_QUIZ_RIVE_PATHS.default;
  const { RiveComponent } = useRive({
    src: getLocalizedAssetPath(src),
    autoplay: true,
  });

  return <RiveComponent key={`${language}-${status}`} />;
};

const QXQuizLoading = ({
  questions = null,
  imagePaths = {
    timeout: "/images/utils/loading/timeout-arial.svg",
  },
  feedbackDuration = 2000,
  end,
  onEnd,
  timeCount,
}) => {
  const { t, language } = useAppTranslation("components/utils/qxQuizLoading");
  const localizedQuestions = t("questions", { returnObjects: true });
  const quizQuestions =
    questions ||
    (Array.isArray(localizedQuestions) ? localizedQuestions : oxQuizQuestion);

  const [currentQuestion, setCurrentQuestion] = useState(() =>
    Math.floor(Math.random() * quizQuestions.length),
  );
  const [imageStatus, setImageStatus] = useState("default");
  const audioRef = useRef(null);
  const [countDown, setCountDown] = useState(timeCount);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showOngoingModal, setShowOngoingModal] = useState(false);
  const [isBackgroundImageChanged, setIsBackgroundImageChanged] =
    useState(false); //NOTE: completed되면 배경 변함

  useEffect(() => {
    if (end) {
      setIsBackgroundImageChanged(true);
      const interval = setInterval(() => {
        onEnd();
      }, 3000);

      return () => {
        clearInterval(interval);
      };
    } else {
      setIsBackgroundImageChanged(false);
    }
  }, [end]);

  const handleAudioPlay = () => {
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        const audio = audioRef.current;
        audio.volume = 0;

        const fadeDuration = 2000;
        const steps = 20;
        const intervalTime = fadeDuration / steps;
        const targetVolume = 0.15;

        let currentStep = 0;

        audio.play().catch((error) => {
          console.error("오디오 재생에 실패했습니다.", error);
        });

        const fadeInterval = setInterval(() => {
          currentStep++;
          const newVolume = (targetVolume / steps) * currentStep;
          audio.volume = Math.min(newVolume, targetVolume);

          if (currentStep >= steps) {
            clearInterval(fadeInterval);
            audio.volume = targetVolume;
          }
        }, intervalTime);
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  // 시계 카운트다운
  useEffect(() => {
    const interval = setInterval(() => {
      setCountDown((prevCountDown) => prevCountDown - 1);
    }, 1000);

    if (countDown === 0) {
      clearInterval(interval);
      setShowOngoingModal(true);
    }
    return () => clearInterval(interval);
  }, [countDown]);

  const handleAnswer = (userAnswer) => {
    if (userAnswer === quizQuestions[currentQuestion].answer) {
      setImageStatus("correct");
      setTimeout(() => {
        setCurrentQuestion(Math.floor(Math.random() * quizQuestions.length));
      }, feedbackDuration);
    } else {
      setImageStatus("wrong");
    }

    setTimeout(() => {
      setImageStatus("default");
    }, feedbackDuration);
  };

  const renderAnimation = () => {
    if (imageStatus === "timeout") {
      const src = imagePaths.timeout;
      return <img src={src} alt={t("alts.quizIllustration")} />;
    }

    return <OxQuizRive status={imageStatus} language={language} />;
  };

  return (
    <div className="quiz-container">
      <audio ref={audioRef} src="/audio/AIMagickid.mp3" loop />
      <div className="clock">
        <span className="time-label">{t("timeRemaining")}</span>
        <figure>
          <img src={"/images/utils/clock.svg"} alt={t("alts.clock")} />
        </figure>
        <p className={`count-down ${countDown <= 10 ? "red" : ""}`}>
          {countDown}
        </p>
      </div>
      <button className="music-play-btn" onClick={handleAudioPlay}>
        <figure>
          <img
            src={
              isPlaying
                ? "/images/utils/button/music-on.svg"
                : "/images/utils/button/music-off.svg"
            }
            alt={isPlaying ? t("alts.musicOn") : t("alts.musicOff")}
          />
        </figure>
      </button>
      <ProgressBar />
      <div className="inner">
        <div className="quiz-box">
          <div className="quiz-title">
            <h1 className="title">{t("title", { defaultValue: "OX Quiz" })}</h1>
          </div>
          <div className="quiz-main">
            <figure className="quiz__image">{renderAnimation()}</figure>
            <div className="quiz-wrap">
              <div className="question">
                {quizQuestions[currentQuestion].question}
              </div>
              <div className="answer-buttons">
                <button className="btn btn-o" onClick={() => handleAnswer("O")}>
                  O
                </button>
                <button className="btn btn-x" onClick={() => handleAnswer("X")}>
                  X
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showOngoingModal && (
        <OngoingModal onclose={() => setShowOngoingModal(false)} />
      )}
    </div>
  );
};

export default QXQuizLoading;
